import React, { useState, useEffect, useRef } from 'react';
import { Spinner } from 'react-bootstrap';
import { getContrastTextColor } from '../utils/ColorUtils';

const OrdersMap = ({ locations, resetTrigger }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [leafletLoaded, setLeafletLoaded] = useState(false);
  const initialViewRef = useRef(null);
  const hasSetInitialView = useRef(false);

  useEffect(() => {
    const loadLeaflet = () => {
      if (!document.querySelector('link[href*="leaflet.css"]')) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(link);
      }

      if (!document.querySelector('script[src*="leaflet.js"]')) {
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.onload = () => setLeafletLoaded(true);
        document.head.appendChild(script);
      } else if (window.L) {
        setLeafletLoaded(true);
      }
    };

    loadLeaflet();
  }, []);

  useEffect(() => {
    if (!leafletLoaded || !mapRef.current) return;
    
    const validLocs = locations.filter(loc => 
      loc.success && loc.latitude != null && loc.longitude != null && 
      !isNaN(loc.latitude) && !isNaN(loc.longitude)
    ).map(loc => ({
      ...loc,
      fullAddress: typeof loc.fullAddress === 'string' ? loc.fullAddress : 'Dirección no disponible'
    }));

    if (mapInstanceRef.current) {
      try {
        mapInstanceRef.current.off();
        mapInstanceRef.current.remove();
      } catch (e) {}
      mapInstanceRef.current = null;
    }

    if (mapRef.current) {
      mapRef.current.innerHTML = '';
      mapRef.current._leaflet_id = null;
    }

    let centerLat = 40.4168;
    let centerLng = -3.7038;
    let zoom = 6;
    
    if (validLocs.length > 0) {
      const lats = validLocs.map(loc => loc.latitude);
      const lngs = validLocs.map(loc => loc.longitude);
      centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
      centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
      zoom = validLocs.length === 1 ? 12 : 8;
    }

    if (!hasSetInitialView.current) {
      initialViewRef.current = { lat: centerLat, lng: centerLng, zoom };
      hasSetInitialView.current = true;
    }

    try {
      const map = window.L.map(mapRef.current, {
        zoomAnimation: false,
        fadeAnimation: false,
        markerZoomAnimation: false
      }).setView([centerLat, centerLng], zoom);
      mapInstanceRef.current = map;

      //window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      var cartoApiKey = process.env.REACT_APP_CARTO_API_KEY;
      const cartoUrl = cartoApiKey
        ? `https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=${cartoApiKey}`
        : 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png';
      window.L.tileLayer(cartoUrl, {
        attribution: '© OpenStreetMap contributors'
      }).addTo(map);

      if (validLocs.length > 0) {
        const group = window.L.featureGroup();
        
        const locationGroups = [];
        const processed = new Set();
        
        validLocs.forEach((location, index) => {
          if (processed.has(index)) return;
          
          const group = [{ ...location, originalIndex: index }];
          processed.add(index);
          
          validLocs.forEach((otherLocation, otherIndex) => {
            if (processed.has(otherIndex)) return;
            
            const latDiff = Math.abs(location.latitude - otherLocation.latitude);
            const lngDiff = Math.abs(location.longitude - otherLocation.longitude);
            
            if (latDiff < 0.001 && lngDiff < 0.001) {
              group.push({ ...otherLocation, originalIndex: otherIndex });
              processed.add(otherIndex);
            }
          });
          
          locationGroups.push(group);
        });
        
        locationGroups.forEach(locations => {
          if (locations.length === 1) {
            const location = locations[0];
            const number = location.originalIndex + 1;
            const order = location.order;
            const isFinalized = order?.status === 'Entregado' || order?.status === 'Albarán firmado';
            const isImportant = order?.isImportant;
            const hasSpecialProducts = order?.hasSpecialProducts;
            const carrierColor = isFinalized ? '#28a745' : (order?.carrier?.colourHex || '#ffffff');
            const textColor = getContrastTextColor(carrierColor);
            
            const numberIcon = window.L.divIcon({
              className: 'custom-number-marker',
              html: `<div style="
                background-color: ${carrierColor};
                color: ${textColor};
                border-radius: 50%;
                width: 30px;
                height: 30px;
                display: flex;
                align-items: center;
                justify-content: center;
                font-weight: bold;
                font-size: 14px;
                border: ${isImportant ? '3px solid #ff6b35' : '2px solid white'};
                box-shadow: ${isImportant ? '0 0 12px rgba(255, 107, 53, 0.8)' : '0 2px 4px rgba(0,0,0,0.3)'};
                ${hasSpecialProducts ? 'outline: 2px dashed #9c27b0; outline-offset: 2px;' : ''}
              ">${number}${isImportant ? '⭐' : ''}</div>`,
              iconSize: [30, 30],
              iconAnchor: [15, 15]
            });
            
            const getProductsHtml = (order) => {
              if (!order || (!order.items?.length && !order.specialProducts?.length)) return '';
              
              let html = '<div style="margin: 10px 0;"><strong>Productos:</strong><br/>';
              
              if (order.items?.length) {
                order.items.forEach(item => {
                  html += `<small>• ${item.quantity}x ${item.productPrice?.type?.typeName || 'N/A'}`;
                  if (item.productPrice?.model?.modelName) html += ` - ${item.productPrice.model.modelName}`;
                  if (item.productPrice?.color?.colorName) html += ` (${item.productPrice.color.colorName})`;
                  html += `</small><br/>`;
                });
              }
              
              if (order.specialProducts?.length) {
                order.specialProducts.forEach(item => {
                  const typeName = item.productType?.typeName || 'Especial';
                  html += `<small>• ${item.quantity || 1}x ${item.description} (${typeName})</small><br/>`;
                });
              }
              
              html += '</div>';
              return html;
            };
            
            const getStatusBadge = (status) => {
              if (!status) return '';
              const isFinalized = status === 'Entregado' || status === 'Albarán firmado';
              const badgeColor = isFinalized ? '#28a745' : '#ffc107';
              const textColor = isFinalized ? 'white' : 'black';
              return `<span style="background: ${badgeColor}; color: ${textColor}; padding: 2px 6px; border-radius: 12px; font-size: 11px; font-weight: bold; margin-left: 8px;">${status}</span>`;
            };
            
            const popupContent = `
              <div style="min-width: 300px; max-width: 400px;">
                <strong style="color: #007bff;">📍 ${order?.externalId || `Pedido #${location.orderId}`}</strong>${order?.status ? getStatusBadge(order.status) : ''}
                ${isImportant ? '<span style="background: linear-gradient(45deg, #ff6b35, #f7931e); color: white; padding: 2px 6px; border-radius: 12px; font-size: 11px; font-weight: bold; margin-left: 4px;">⭐ IMPORTANTE</span>' : ''}
                ${hasSpecialProducts ? '<span style="background: #9c27b0; color: white; padding: 2px 6px; border-radius: 12px; font-size: 11px; font-weight: bold; margin-left: 4px;">🔷 ESPECIAL</span>' : ''}
                ${location.address?.exactGeoMatch === false ? '<br/><div style="background: #fff3cd; border: 1px solid #ffeaa7; border-radius: 4px; padding: 8px; margin: 8px 0;"><strong style="color: #856404;">⚠️ UBICACIÓN APROXIMADA</strong><br/><small style="color: #856404;">La dirección puede no ser exacta</small></div>' : '<br/><br/>'}
                ${order ? `<strong>Cliente:</strong> ${order.client?.name || ''} ${order.client?.lastName1 || ''}<br/>` : ''}
                ${order ? `<strong>Total:</strong> ${order.totalAmount}€<br/>` : ''}
                ${order && order.carrier ? `<strong>Transportista:</strong> ${order.carrier.firstName} ${order.carrier.lastName1}<br/>` : order ? `<strong>Transportista:</strong> Sin Asignar<br/>` : ''}
                ${order ? getProductsHtml(order) : ''}
                ${location.fullAddress ? `<strong>Dirección:</strong><br/>${location.fullAddress}<br/>` : ''}
              </div>
            `;
            
            const marker = window.L.marker([location.latitude, location.longitude], { icon: numberIcon })
              .bindPopup(popupContent);
            group.addLayer(marker);
          } else {
            const centerLat = locations.reduce((sum, loc) => sum + loc.latitude, 0) / locations.length;
            const centerLng = locations.reduce((sum, loc) => sum + loc.longitude, 0) / locations.length;
            const hasFinalized = locations.some(loc => loc.order?.status === 'Entregado' || loc.order?.status === 'Albarán firmado');
            const firstCarrierColor = hasFinalized ? '#28a745' : (locations[0].order?.carrier?.colourHex || '#6c757d');
            const textColor = getContrastTextColor(firstCarrierColor);
            
            // Obtener el número más bajo de los pedidos en este grupo
            const lowestOrderNumber = Math.min(...locations.map(loc => loc.originalIndex + 1));
            
            const hasImportantInGroup = locations.some(loc => loc.order?.isImportant);
            const hasSpecialInGroup = locations.some(loc => loc.order?.hasSpecialProducts);
            
            const clusterIcon = window.L.divIcon({
              className: 'custom-cluster-marker',
              html: `<div style="
                background-color: ${firstCarrierColor};
                color: ${textColor};
                border-radius: 50%;
                width: 35px;
                height: 35px;
                display: flex;
                align-items: center;
                justify-content: center;
                font-weight: bold;
                font-size: 12px;
                border: ${hasImportantInGroup ? '3px solid #ff6b35' : '2px solid white'};
                box-shadow: ${hasImportantInGroup ? '0 0 12px rgba(255, 107, 53, 0.8)' : '0 2px 4px rgba(0,0,0,0.3)'};
                ${hasSpecialInGroup ? 'outline: 2px dashed #9c27b0; outline-offset: 2px;' : ''}
              ">${lowestOrderNumber}<sup style="font-size: 8px;">${locations.length}</sup></div>`,
              iconSize: [35, 35],
              iconAnchor: [17, 17]
            });
            
            const getProductsHtml = (order) => {
              if (!order || (!order.items?.length && !order.specialProducts?.length)) return '';
              
              let html = '<div style="margin: 5px 0;"><small><strong>Productos:</strong><br/>';
              
              if (order.items?.length) {
                order.items.forEach(item => {
                  html += `• ${item.quantity}x ${item.productPrice?.type?.typeName || 'N/A'}<br/>`;
                });
              }
              
              if (order.specialProducts?.length) {
                order.specialProducts.forEach(item => {
                  const typeName = item.productType?.typeName || 'Especial';
                  html += `• ${item.quantity || 1}x ${item.description} (${typeName})<br/>`;
                });
              }
              
              html += '</small></div>';
              return html;
            };
            
            const getStatusBadge = (status) => {
              if (!status) return '';
              const isFinalized = status === 'Entregado' || status === 'Albarán firmado';
              const badgeColor = isFinalized ? '#28a745' : '#ffc107';
              const textColor = isFinalized ? 'white' : 'black';
              return `<span style="background: ${badgeColor}; color: ${textColor}; padding: 1px 4px; border-radius: 8px; font-size: 10px; font-weight: bold; margin-left: 4px;">${status}</span>`;
            };
            
            const popupContent = `
              <div style="min-width: 300px; max-width: 450px;">
                <strong style="color: #6c757d;">📍 ${locations.length} Pedidos cercanos</strong><br/><br/>
                ${locations.map(loc => {
                  const order = loc.order;
                  const isImportant = order?.isImportant;
                  const hasSpecialProducts = order?.hasSpecialProducts;
                  return `
                    <div style="margin-bottom: 10px; padding: 8px; border: 1px solid #ddd; border-radius: 4px; ${isImportant ? 'border-left: 4px solid #ff6b35;' : ''} ${hasSpecialProducts ? 'background: #f3e5f5;' : ''}">
                      <strong>${order?.externalId || `Pedido #${loc.orderId}`}</strong> (${loc.originalIndex + 1})${order?.status ? getStatusBadge(order.status) : ''}
                      ${isImportant ? '<span style="background: linear-gradient(45deg, #ff6b35, #f7931e); color: white; padding: 1px 4px; border-radius: 8px; font-size: 10px; font-weight: bold; margin-left: 4px;">⭐</span>' : ''}
                      ${hasSpecialProducts ? '<span style="background: #9c27b0; color: white; padding: 1px 4px; border-radius: 8px; font-size: 10px; font-weight: bold; margin-left: 4px;">🔷</span>' : ''}<br/>
                      ${order ? `<small><strong>${order.client?.name || ''} ${order.client?.lastName1 || ''}</strong> - ${order.totalAmount}€</small><br/>` : ''}
                      ${order && order.carrier ? `<small><strong>Transportista:</strong> ${order.carrier.firstName} ${order.carrier.lastName1}</small><br/>` : order ? `<small><strong>Transportista:</strong> Sin Asignar</small><br/>` : ''}
                      ${order ? getProductsHtml(order) : ''}
                      ${loc.address?.exactGeoMatch === false ? '<div style="background: #fff3cd; border-radius: 3px; padding: 4px; margin: 4px 0;"><small style="color: #856404; font-weight: bold;">⚠️ UBICACIÓN APROXIMADA</small></div>' : ''}
                      ${loc.fullAddress ? `<small>${loc.fullAddress}</small>` : ''}
                    </div>
                  `;
                }).join('')}
              </div>
            `;
            
            const marker = window.L.marker([centerLat, centerLng], { icon: clusterIcon })
              .bindPopup(popupContent);
            group.addLayer(marker);
          }
        });
        
        group.addTo(map);
        
        if (validLocs.length > 1) {
          const bounds = group.getBounds();
          const zoom = map.getBoundsZoom(bounds, false) - 1;
          const center = bounds.getCenter();
          map.setView(center, zoom);
        }
        
        const focusLocationHandler = (event) => {
          const { location, number } = event.detail;
          map.setView([location.latitude, location.longitude], 16);
          
          group.eachLayer((layer) => {
            const layerLatLng = layer.getLatLng();
            if (Math.abs(layerLatLng.lat - location.latitude) < 0.00001 && 
                Math.abs(layerLatLng.lng - location.longitude) < 0.00001) {
              layer.openPopup();
            }
          });
        };
        
        window.addEventListener('focusLocation', focusLocationHandler);
        
        const beforePrintHandler = () => {
          setTimeout(() => {
            map.invalidateSize();
          }, 200);
        };
        
        const finalPrintAdjustHandler = () => {
          setTimeout(() => {
            map.invalidateSize();
          }, 100);
        };
        
        window.addEventListener('beforePrint', beforePrintHandler);
        window.addEventListener('finalPrintAdjust', finalPrintAdjustHandler);
        
        return () => {
          window.removeEventListener('focusLocation', focusLocationHandler);
          window.removeEventListener('beforePrint', beforePrintHandler);
          window.removeEventListener('finalPrintAdjust', finalPrintAdjustHandler);
        };
      }
    } catch (error) {}

    return () => {
      if (mapInstanceRef.current) {
        try {
          mapInstanceRef.current.off();
          mapInstanceRef.current.remove();
        } catch (e) {}
        mapInstanceRef.current = null;
      }
    };
  }, [leafletLoaded, locations]);

  useEffect(() => {
    if (resetTrigger > 0 && mapInstanceRef.current && initialViewRef.current) {
      const { lat, lng, zoom } = initialViewRef.current;
      mapInstanceRef.current.setView([lat, lng], zoom);
    }
  }, [resetTrigger]);

  if (locations.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center h-100">
        <div className="text-center">
          <i className="bi bi-funnel" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
          <p className="mt-2 text-muted mb-1">Selecciona filtros para ver pedidos</p>
          <small className="text-muted">Los pedidos aparecerán aquí cuando apliques los filtros</small>
        </div>
      </div>
    );
  }

  if (!leafletLoaded) {
    return (
      <div className="d-flex justify-content-center align-items-center h-100">
        <Spinner animation="border" />
        <span className="ms-2">Cargando mapa...</span>
      </div>
    );
  }

  return <div ref={mapRef} className="w-100 h-100" />;
};

export default OrdersMap;